import { useI18n } from "@core/i18n/i18n";
import { Patient, PendingPatient } from "@domain/patient/patient";
import { AllPatientPrograms, PatientProgram } from "@domain/program/patient-program";
import { AnalyticsProgramLocation, sendAnalyticsEventCreateProgram } from "@domain/program/program-analytics";
import { GlobalProgramStatusType, ProgramStatus } from "@domain/program/program-status/program-status";
import styled from "@emotion/styled";
import { AlertCard, CardType } from "@ui/components/common/alert-card";
import { ConditionalSkeleton } from "@ui/components/common/conditional-skeleton";
import { NetworkError } from "@ui/components/common/error";
import { Panel } from "@ui/components/common/panel";
import { PatientProgramCard } from "@ui/components/program/program-card";
import { ProgramSkeleton } from "@ui/components/common/program-skeleton";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { LoadingEmptyScreen, LoadingSize } from "@ui/pages/login/loading-empty-screen";
import {
	CreateProgramButton,
	CreateProgramButtonType,
} from "@ui/pages/patient-details/components/create-program-button";
import { EmptyProgramsPlaceholder } from "@ui/pages/patient-details/components/program-tab/empty-programs-placeholder";
import { useAllPatientPrograms } from "@ui/pages/program-builder/hooks/use-patient-program";
import { useProgramActions } from "@ui/pages/program-builder/hooks/use-program-actions";
import { physioMeMobileBreakpoint, physioMeTabletBreakpoint, row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import { truncateString } from "@utils/string-utils";
import React, { useCallback } from "react";
import { useProgramCard } from "@ui/components/program/hooks/use-program-card";

export const ProgramTab: React.FC<{ patient: Patient | PendingPatient }> = ({ patient }) => {
	const { format } = useI18n();
	const {
		allPatientPrograms,
		loading,
		shouldDisplayProgramEndingSoonAlert,
		shouldDisplayProgramEndingNoOngoingAlert,
		refreshAllPrograms,
	} = useAllPatientPrograms(patient);

	const { extendProgram } = useProgramActions();
	const truncatedPatientName = truncateString(patient.firstName, 20);
	const { isMobile } = useDimensions();

	if (!allPatientPrograms) {
		return loading ? (
			<LoadingEmptyScreen size={LoadingSize.FullSpace} />
		) : (
			<NetworkError
				style={{ marginTop: 150 }}
				onRefresh={async () => {
					await refreshAllPrograms(patient.id);
				}}
			/>
		);
	}

	const hasNoPrograms =
		allPatientPrograms.ongoingProgram === undefined &&
		allPatientPrograms.endedPrograms.length === 0 &&
		allPatientPrograms.scheduledPrograms.length === 0 &&
		allPatientPrograms.draftPrograms.length === 0;

	const skeletonProps = { active: hasNoPrograms, loading };
	const shouldHideContent = isMobile && hasNoPrograms;

	let alertContent: JSX.Element | null = null;
	if (shouldDisplayProgramEndingSoonAlert) {
		alertContent = (
			<AlertCard
				title={format("patientDetails.alert.programEndingSoon.title", {
					patientName: truncatedPatientName,
				})}
				message={format("patientDetails.alert.programEndingSoon.message")}
				cardType={CardType.Warn}
				secondaryButtons={[
					{
						title: format("patientDetails.alert.programEndingSoon.button"),
						onPressed: () => {
							if (isNotNil(allPatientPrograms.ongoingProgram)) {
								extendProgram(
									AnalyticsProgramLocation.PROGRAM_TAB,
									patient.id,
									patient.analyticsId,
									allPatientPrograms.ongoingProgram.id,
									GlobalProgramStatusType.EndingSoonProgramWithNoNext,
								);
							}
						},
					},
				]}
			/>
		);
	} else if (shouldDisplayProgramEndingNoOngoingAlert) {
		alertContent = (
			<AlertCard
				title={format("patientDetails.alert.programEnded.title", { patientName: truncatedPatientName })}
				message={format("patientDetails.alert.programEnded.message")}
				cardType={CardType.Warn}
			/>
		);
	}

	return (
		<Container>
			{hasNoPrograms && (
				<EmptyProgramsPlaceholder
					patient={patient}
					analyticsLocation={AnalyticsProgramLocation.PROGRAM_TAB_BOTTOM}
					style={{ position: shouldHideContent ? "static" : "absolute" }}
				/>
			)}
			{!shouldHideContent && (
				<>
					<TabHeaderContainer>
						<ConditionalSkeleton {...skeletonProps} skeletonStyle={{ borderRadius: "0.5rem" }}>
							<p className={isMobile ? "p-size-l-bold" : "p-header-3"}>{format("patientDetails.tabs.programs")}</p>
						</ConditionalSkeleton>
						<ConditionalSkeleton {...skeletonProps} skeletonStyle={{ borderRadius: "0.5rem" }}>
							<CreateProgramButton
								patient={patient}
								buttonType={CreateProgramButtonType.NewOrFromTemplate}
								onClick={fromTemplate =>
									sendAnalyticsEventCreateProgram(
										AnalyticsProgramLocation.PROGRAM_TAB_TOP,
										patient.analyticsId,
										fromTemplate,
									)
								}
							/>
						</ConditionalSkeleton>
					</TabHeaderContainer>
					<ConditionalSkeleton {...skeletonProps} skeletonStyle={{ borderRadius: "0.5rem" }}>
						{alertContent}
					</ConditionalSkeleton>
					<AllPatientProgramsPanels
						allPatientPrograms={allPatientPrograms}
						patient={patient}
						hasNoPrograms={hasNoPrograms}
						loading={loading}
					/>
				</>
			)}
		</Container>
	);
};

const AllPatientProgramsPanels: React.FC<{
	allPatientPrograms: AllPatientPrograms;
	patient: Patient | PendingPatient;
	hasNoPrograms: boolean;
	loading: boolean;
}> = ({ allPatientPrograms, patient, hasNoPrograms, loading }) => {
	const { format } = useI18n();
	const { lastProgramAdded, navigateToProgram, getProgramCardActions } = useProgramCard();

	const { ongoingProgram, scheduledPrograms, draftPrograms, endedPrograms } = allPatientPrograms;

	const displayProgramCardListing = useCallback(
		(programs: PatientProgram[]) =>
			programs.length > 0 && (
				<CardsContainer>
					{programs.map(program => (
						<PatientProgramCard
							key={program.id}
							program={program}
							cardActions={getProgramCardActions(patient, program)}
							onClick={() => navigateToProgram(patient, program)}
							isNew={program.id === lastProgramAdded}
						/>
					))}
				</CardsContainer>
			),
		[getProgramCardActions, lastProgramAdded, navigateToProgram, patient],
	);
	const skeletonProps = { active: hasNoPrograms, loading };
	return (
		<>
			<ProgramSkeleton {...skeletonProps}>
				<Panel
					id="ongoing"
					headerText={format("patientDetails.patientPrograms.ongoingPanel.title", { count: ongoingProgram ? 1 : 0 })}
					tooltip={<span>{format("patientDetails.patientPrograms.ongoingPanel.tooltip.v1_6")}</span>}
					showToggleButton={false}
				>
					{ongoingProgram && (
						<OngoingLayout>
							<PatientProgramCard
								program={{ ...ongoingProgram, status: ProgramStatus.Ongoing }}
								cardActions={getProgramCardActions(patient, ongoingProgram)}
								onClick={() => navigateToProgram(patient, ongoingProgram)}
								isNew={ongoingProgram.id === lastProgramAdded}
							/>
						</OngoingLayout>
					)}
				</Panel>
			</ProgramSkeleton>
			<ProgramSkeleton {...skeletonProps}>
				<Panel
					id="future"
					headerText={format("patientDetails.patientPrograms.scheduledPanel.title", {
						count: scheduledPrograms.length,
					})}
					tooltip={<span>{format("patientDetails.patientPrograms.futurePanel.tooltip.v1_6")}</span>}
					showToggleButton={false}
				>
					{displayProgramCardListing(scheduledPrograms)}
				</Panel>
			</ProgramSkeleton>
			<ProgramSkeleton {...skeletonProps}>
				<Panel
					id="draft"
					headerText={format("patientDetails.patientPrograms.draftPanel.title", { count: draftPrograms.length })}
					tooltip={<span>{format("patientDetails.patientPrograms.draftPanel.tooltip.v1_6")}</span>}
					showToggleButton={true}
				>
					{displayProgramCardListing(draftPrograms)}
				</Panel>
			</ProgramSkeleton>
			<ProgramSkeleton {...skeletonProps}>
				<Panel
					id="former"
					headerText={format("patientDetails.patientPrograms.endedPanel.title", { count: endedPrograms.length })}
					tooltip={<span>{format("patientDetails.patientPrograms.formerPanel.tooltip.v1_6")}</span>}
					showToggleButton={true}
				>
					{displayProgramCardListing(endedPrograms)}
				</Panel>
			</ProgramSkeleton>
		</>
	);
};

const Container = styled.div`
	${stack(40, "center", "stretch")};
	flex-grow: 1;
	position: relative;
`;

const TabHeaderContainer = styled.div`
	${row(undefined, "spacebetween", "center")};
	@media ${physioMeMobileBreakpoint} {
		${stack(24)};
		margin-top: 8px;
	}
`;

const CardsContainer = styled.div`
	padding-top: 16px;
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
	grid-template-rows: min-content;
	grid-gap: 32px 16px;

	@media ${physioMeTabletBreakpoint} {
		grid-gap: 16px 24px;
	}
	@media ${physioMeMobileBreakpoint} {
		grid-template-columns: 1fr;
	}
`;

const OngoingLayout = styled(CardsContainer)`
	background-color: ${colors.primaryBackground};
	border-radius: 0 0 8px 8px;
	padding: 16px;
`;
